/**
 * AdminAthletesDuplicatesPage — /admin/athletes/duplicates (Sprint 3 Lot A.3)
 *
 * Vue institutionnelle agrégeant les match_probable de tous les batches
 * non-rollbackés, non-encore décidés. Filtres, actions COPF/Admin, bulk safe.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { showError } from "@/lib/errors";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldAlert, ArrowLeft, Copy, Link as LinkIcon, X, Users, GitMerge, Filter } from "lucide-react";

export default function AdminAthletesDuplicatesPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const roles = user?.roles || [];
  const isModerator = roles.some((r) => ["admin", "copf"].includes(r));
  const isAdmin = roles.includes("admin");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filters, setFilters] = useState({ source_key: "", discipline: "", batch_id: "", status: "pending" });
  const [selected, setSelected] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const { data } = await api.get(`/admin/athletes/duplicates?${params}`);
      setItems(data.items || []);
      setSelected(new Set());
    } catch (e) { showError(e, { prefix: "Doublons :" }); }
    finally { setLoading(false); }
  }, [filters, refreshKey]);
  useEffect(() => { load(); }, [load]);

  const decide = async (item, decision, note = null) => {
    try {
      await api.post("/admin/athletes/duplicates/decision", {
        batch_id: item.batch.id,
        existing_athlete_id: item.existing.id,
        line_full_name: item.line?.line_full_name,
        decision,
        note,
      });
      toast.success("Décision enregistrée.");
      setRefreshKey((k) => k + 1);
    } catch (e) { showError(e, { prefix: "Décision :" }); }
  };

  const bulkIgnore = async () => {
    if (selected.size === 0) { toast.error("Sélectionnez au moins un doublon."); return; }
    const chosen = items.filter((it) => selected.has(it.case_key));
    try {
      const { data } = await api.post("/admin/athletes/duplicates/bulk", {
        decisions: chosen.map((it) => ({
          batch_id: it.batch.id,
          existing_athlete_id: it.existing.id,
          line_full_name: it.line?.line_full_name,
          decision: "ignore",
        })),
      });
      toast.success(`${data.applied_count} décision(s) appliquées · ${data.error_count} erreur(s).`);
      setRefreshKey((k) => k + 1);
    } catch (e) { showError(e, { prefix: "Bulk ignore :" }); }
  };

  const goMerge = (item) => {
    // On propose l'athlète existant comme cible ; source à choisir dans l'écran dédié
    nav(`/admin/athletes/merge?target=${encodeURIComponent(item.existing.id)}&from=duplicate`);
  };

  const toggle = (case_key) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(case_key)) next.delete(case_key); else next.add(case_key);
      return next;
    });
  };
  const allSelected = items.length > 0 && items.every((it) => selected.has(it.case_key));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((it) => it.case_key)));

  if (!isModerator) {
    return (
      <div className="max-w-2xl mx-auto p-6" data-testid="duplicates-access-denied">
        <Card className="p-6 text-center">
          <ShieldAlert className="mx-auto h-10 w-10 text-destructive mb-2" />
          <h1 className="text-xl font-semibold">Accès refusé</h1>
          <p className="text-sm text-muted-foreground mt-1">Réservé aux admins et à la COPF.</p>
          <Button className="mt-4" variant="outline" onClick={() => nav("/")}><ArrowLeft className="h-4 w-4 mr-1.5" />Retour</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4" data-testid="admin-duplicates-page">
      <div>
        <h1 className="text-2xl font-display font-bold flex items-center gap-2">
          <Copy className="h-6 w-6 text-[hsl(var(--aito-energy))]" />Doublons à traiter
          <Badge variant="outline" className="ml-2 font-mono">{items.length}</Badge>
        </h1>
        <p className="text-sm text-muted-foreground">Agrégation de tous les <em>match_probable</em> des imports en cours. Traitez les cas simples ici, escaladez la fusion vers un admin.</p>
      </div>

      {/* Filtres */}
      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Input placeholder="source_key" value={filters.source_key} onChange={(e) => setFilters({ ...filters, source_key: e.target.value })} className="h-9 w-40" data-testid="filter-source-key" />
          <Input placeholder="discipline (slug)" value={filters.discipline} onChange={(e) => setFilters({ ...filters, discipline: e.target.value })} className="h-9 w-40" data-testid="filter-discipline" />
          <Input placeholder="batch_id" value={filters.batch_id} onChange={(e) => setFilters({ ...filters, batch_id: e.target.value })} className="h-9 w-56 font-mono text-xs" data-testid="filter-batch" />
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="h-9 w-40" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">À traiter</SelectItem>
              <SelectItem value="decided">Décidés</SelectItem>
              <SelectItem value="all">Tous</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={() => setFilters({ source_key: "", discipline: "", batch_id: "", status: "pending" })}>Réinitialiser</Button>
        </div>
      </Card>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <Card className="p-3 bg-secondary/40 border-dashed" data-testid="bulk-actions-bar">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span>{selected.size} sélectionné(s)</span>
            <Button size="sm" variant="outline" onClick={bulkIgnore} data-testid="bulk-ignore"><X className="h-3.5 w-3.5 mr-1" />Ignorer en lot</Button>
            <span className="text-xs text-muted-foreground ml-2">La fusion ne peut jamais être exécutée en lot.</span>
          </div>
        </Card>
      )}

      {/* Table */}
      {loading ? (
        <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">Aucun doublon à traiter avec ces filtres.</Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"><Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="tout sélectionner" /></TableHead>
                <TableHead>Ligne fichier</TableHead>
                <TableHead>Profil existant</TableHead>
                <TableHead className="hidden md:table-cell">Discipline</TableHead>
                <TableHead className="hidden md:table-cell">Batch</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.case_key} data-testid={`dup-row-${it.case_key}`}>
                  <TableCell><Checkbox checked={selected.has(it.case_key)} onCheckedChange={() => toggle(it.case_key)} /></TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{it.line?.line_full_name || "—"}</div>
                    <div className="text-[11px] text-muted-foreground">{it.line?.reason}</div>
                  </TableCell>
                  <TableCell>
                    <Link to={`/athletes/${it.existing.slug || it.existing.id}`} target="_blank" className="text-sm font-medium hover:underline text-[hsl(var(--aito-lagoon))]">{it.existing.full_name}</Link>
                    <div className="text-[11px] text-muted-foreground">
                      {it.existing.claim_status === "claimed" ? <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">revendiqué</Badge> : <Badge variant="outline" className="text-[10px]">institutionnel</Badge>}
                      {it.existing.import_source_key && <span className="ml-1 font-mono">{it.existing.import_source_key}</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs hidden md:table-cell">{it.existing.main_discipline || "—"}</TableCell>
                  <TableCell className="text-xs hidden md:table-cell">
                    <div className="font-mono">{it.batch.source_key}</div>
                    <div className="text-[10px] text-muted-foreground">{it.batch.id.slice(0, 8)}…</div>
                  </TableCell>
                  <TableCell className="text-right">
                    {it.decision ? (
                      <Badge variant="outline" className="text-[10px]">Décidé : {it.decision.decision}</Badge>
                    ) : (
                      <div className="inline-flex gap-1 flex-wrap justify-end">
                        <Button size="sm" variant="ghost" onClick={() => decide(it, "link_existing")} data-testid={`dup-link-${it.case_key}`}><LinkIcon className="h-3.5 w-3.5 mr-1" />Utiliser l'existant</Button>
                        <Button size="sm" variant="ghost" onClick={() => decide(it, "ignore")} data-testid={`dup-ignore-${it.case_key}`}><X className="h-3.5 w-3.5 mr-1" />Ignorer</Button>
                        <Button size="sm" variant="ghost" onClick={() => decide(it, "different_people")} data-testid={`dup-different-${it.case_key}`}><Users className="h-3.5 w-3.5 mr-1" />Différents</Button>
                        {isAdmin ? (
                          <Button size="sm" variant="outline" onClick={() => goMerge(it)} data-testid={`dup-merge-${it.case_key}`}><GitMerge className="h-3.5 w-3.5 mr-1" />Fusionner…</Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => decide(it, "flag_for_merge")} data-testid={`dup-flag-${it.case_key}`}><GitMerge className="h-3.5 w-3.5 mr-1" />Signaler pour fusion</Button>
                        )}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
